import { useEffect, useRef } from "react";

import { mobile } from "./mobile";
import { registerBackHandler } from "./mobileBackHandlerRegistry";

/**
 * Claims the mobile native back button for the lifetime of the calling component
 * (while `enabled`). This lets a state-based overlay — modal, drawer, bottom
 * sheet — be dismissed with the Android hardware/gesture back button (or the
 * WebView header back), which is the only affordance available once the close
 * button and mask are hidden in the WebView.
 *
 * Handlers are held in a last-in-first-out stack, so nested overlays (e.g. a
 * drawer opened over a modal) unwind last-opened-first: back press runs the
 * most-recently-mounted handler. The handler is registered on mount and removed
 * on unmount (or when `enabled` becomes false), keeping the active state
 * accurate. Outside the mobile WebView the hook is a no-op and safe to call —
 * nothing is registered, so there is no back button to claim.
 *
 * The `handler` is held in a ref, so passing a new inline function each render
 * does NOT tear down and re-register the stack entry — the LIFO order stays
 * stable across re-renders.
 *
 * **Known limitation (not enforced):** this supports only UI where relative
 * route navigation cannot occur while a handler is registered. Navigating with a
 * handler active throws off back behavior, because the handler stays on the stack
 * across the navigation. A runtime warning for this case was deliberately
 * deferred — detecting it requires subscribing to route-change history, which is
 * heavier than this hook warrants. If a nav-while-registered use case ever
 * arises, extend this hook to auto-dismiss on route change.
 *
 * @param handler Called when native back is pressed while this is the topmost
 *   registered handler. Typically closes the overlay.
 * @param enabled When false, nothing is registered (e.g. gate on the overlay's
 *   open state). Defaults to true.
 *
 * @example
 * ```tsx
 * useMobileBackHandler(onClose, isOpen);
 * ```
 */
export function useMobileBackHandler(
  handler: () => void,
  enabled = true,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled || !mobile.isWebview) return;

    return registerBackHandler(() => handlerRef.current());
  }, [enabled]);
}
